import React, { type ReactNode, useState, useRef } from 'react';
import classNames from 'classnames';
import { useTranslation } from 'react-i18next';
import useOpaqueId from '@jwp/ott-hooks-react/src/useOpaqueId';
import CloseIcon from '@jwp/ott-theme/assets/icons/close.svg?react';
import DropdownIcon from '@jwp/ott-theme/assets/icons/Dropdown.svg?react';

import { useTheme } from '../../../../../ThemeContext';
import HelperText from '../HelperText/HelperText';
import DetectOutsideClick from '../DetectOutsideClick/DetectOutsideClick';
import Icon from '../Icon/Icon';

import styles from './Dropdown.module.scss';

export type Props = Omit<React.SelectHTMLAttributes<HTMLSelectElement>, 'size' | 'onChange'> & {
  className?: string;
  defaultLabel?: string;
  options?: (string | { value: string; label: string })[];
  optionsStyle?: string;
  label?: ReactNode;
  fullWidth?: boolean;
  size?: 'small' | 'medium';
  error?: boolean;
  helperText?: string;
  required?: boolean;
  hideOptional?: boolean;
  testId?: string;
  onChange: (event: React.ChangeEvent<HTMLSelectElement>) => void;
  lang?: string;
  multiSelect?: boolean;
};

const Dropdown: React.FC<Props> = ({
  name,
  value,
  className,
  defaultLabel,
  options,
  onChange,
  label,
  fullWidth,
  error,
  helperText,
  required = false,
  size = 'medium',
  testId,
  hideOptional,
  disabled,
  lang,
  multiSelect,
  ...rest
}: Props & React.AriaAttributes) => {
  const { t } = useTranslation('common');
  const { universalStyles } = useTheme();
  const id = useOpaqueId();
  const helperTextId = useOpaqueId('helper_text', name);
  const [isOpen, setIsOpen] = useState(false);
  const triggerRef = useRef<HTMLDivElement>(null);
  const [openUpward, setOpenUpward] = useState(false);
  // const handleToggle = () => {
  //   if (!disabled) {
  //     setIsOpen((prev) => !prev);
  //   }
  // };

  const handleToggle = () => {
    if (disabled) { return; }

    if (!isOpen && triggerRef.current) {
      const rect = triggerRef.current.getBoundingClientRect();

      // Find nearest scrollable parent
      const scrollParent = triggerRef.current.closest('[data-scroll-container]');

      const parentRect = scrollParent
        ? scrollParent.getBoundingClientRect()
        : {
            top: 0,
            bottom: window.innerHeight,
          };

      const optionsCount = options?.length || 0;
      const optionHeight = 57;
      const extraPadding = 16;

      const dropdownHeight = Math.min(optionsCount, 4) * optionHeight + extraPadding;

      const spaceBelow = parentRect.bottom - rect.bottom;
      const spaceAbove = rect.top - parentRect.top;

      const shouldOpenUpward = spaceBelow < dropdownHeight && spaceAbove > spaceBelow;

      setOpenUpward(shouldOpenUpward);
    }

    setIsOpen((prev) => !prev);
  };

  const handleOptionClick = (optionValue: string) => {
    if (multiSelect) {
      let currentValues: string[] = [];
      if (Array.isArray(value)) {
        currentValues = value;
      } else if (value) {
        currentValues = [value as string];
      }

      const isSelected = currentValues.includes(optionValue);
      const newValues = isSelected ? currentValues.filter((v) => v !== optionValue) : [...currentValues, optionValue];

      const event = {
        target: {
          name,
          value: newValues,
        },
      } as unknown as React.ChangeEvent<HTMLSelectElement>;

      onChange(event);
    } else {
      const event = {
        target: {
          name,
          value: optionValue,
        },
      } as React.ChangeEvent<HTMLSelectElement>;

      onChange(event);
      setIsOpen(false);
    }
  };

  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (disabled) { return; }

    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      setIsOpen((prev) => !prev);
    } else if (event.key === 'Escape') {
      setIsOpen(false);
    }
  };

  const isSelected = (optValue: string) => {
    if (multiSelect) {
      return Array.isArray(value) && value.includes(optValue);
    }
    return value === optValue;
  };

  const selectedOption = options?.find((opt) => (typeof opt === 'string' ? opt === value : opt.value === value));

  let displayLabel = defaultLabel || '';
  if (!multiSelect) {
    if (typeof selectedOption === 'string') {
      displayLabel = selectedOption;
    } else if (selectedOption?.label) {
      displayLabel = selectedOption.label;
    }
  }

  return (
    <div
      className={classNames(styles.container, { [styles.fullWidth]: fullWidth, [styles.error]: error, [styles.disabled]: disabled }, styles[size], className)}
      data-testid={testId}
    >
      {(label || (!required && !hideOptional)) && (
        <div className={styles.label}>
          {label}
          {required ? (
            <span className={styles.required} style={universalStyles.errorColor}>
              *
            </span>
          ) : null}
          {!required && !hideOptional ? <span>{t('optional')}</span> : null}
        </div>
      )}
      <div className={styles.dropdownWrapper} ref={triggerRef}>
        <button
          type="button"
          id={id}
          className={classNames(styles.dropdown, { [styles.fullWidth]: fullWidth, [styles.disabled]: disabled, [styles.open]: isOpen })}
          onClick={handleToggle}
          onKeyDown={handleKeyDown}
          disabled={disabled}
          aria-haspopup="listbox"
          aria-expanded={isOpen}
          lang={lang}
          {...(rest as React.ButtonHTMLAttributes<HTMLButtonElement>)}
        >
          <div className={classNames(styles.select, { [styles.placeholderSelected]: multiSelect ? !Array.isArray(value) || value.length === 0 : !value })}>
            {displayLabel}
          </div>
          <span className={styles.dropdownIcon} aria-hidden>
            <Icon icon={DropdownIcon} />
          </span>
        </button>

        {isOpen && (
          <DetectOutsideClick callback={() => setIsOpen(false)}>
            <div
              className={classNames(styles.popover, styles.optionsList, {
                [styles.openUpward]: openUpward,
                [styles.openDownward]: !openUpward,
              })}
              role="listbox"
            >
              {defaultLabel && !required && (
                <li
                  className={classNames(styles.option, styles.dropdownOption)}
                  onClick={() => handleOptionClick('')}
                  role="option"
                  aria-selected={value === ''}
                  tabIndex={0}
                >
                  {defaultLabel}
                </li>
              )}
              {options?.map((option) => {
                const optValue = typeof option === 'string' ? option : option.value;
                const optLabel = typeof option === 'string' ? option : option.label;
                return (
                  <li
                    key={optValue}
                    className={classNames(styles.option, styles.dropdownOption, { [styles.selected]: isSelected(optValue) })}
                    onClick={() => handleOptionClick(optValue)}
                    role="option"
                    tabIndex={0}
                    aria-selected={isSelected(optValue)}
                  >
                    {optLabel}
                  </li>
                );
              })}
            </div>
          </DetectOutsideClick>
        )}
      </div>

      {multiSelect && Array.isArray(value) && value.length > 0 && (
        <div className={styles.tagListBelow}>
          {value.map((v) => {
            const option = options?.find((opt) => (typeof opt === 'string' ? opt === v : opt.value === v));
            const labelStr = typeof option === 'string' ? option : option?.label || v;
            return (
              <div key={v} className={styles.tag}>
                <span>{labelStr}</span>
                <div className={styles.removeTag} onClick={() => handleOptionClick(v)}>
                  <Icon icon={CloseIcon} />
                </div>
              </div>
            );
          })}
        </div>
      )}

      <HelperText id={helperTextId} error={error}>
        {helperText}
      </HelperText>
    </div>
  );
};

export default Dropdown;
